// © 2026 AttainIP Engineering

import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import rehypeRaw from 'rehype-raw';
import type { ChatMessage } from './types';

interface MessageBubbleProps {
  message: ChatMessage;
}

/** Strip ::submit[...](...)::: tokens (rendered as buttons elsewhere) */
function stripSubmitTokens(text: string): string {
  return text.replace(/::submit\[[^\]]+?\]\([^)]*?\)::/g, '').trimEnd();
}

/**
 * Transform inline tokens for assistant rendering (RFC-0153):
 *
 * @-refs  → styled badge (non-clickable)
 * #[...]  → document name badge
 * ::button:id:: → stripped
 */
function transformInlineTokens(text: string): string {
  // @-refs → maroon badge
  let result = text.replace(
    /(?<!\w)@([A-Za-z0-9][A-Za-z0-9._-]*(?::[A-Za-z0-9][A-Za-z0-9._-]*)*)/g,
    '<span style="display:inline;padding:1px 5px;margin:0 1px;font-size:11px;font-family:ui-monospace,monospace;font-weight:500;color:#77010D;background:#fdf2f2;border:1px solid #e8c4c4;border-radius:4px;white-space:nowrap">@$1</span>',
  );

  // #[...] citations → document name badge
  result = result.replace(
    /#\[([^\]]*)\]/g,
    (_match, inner) => {
      try {
        const fields = JSON.parse(`[${inner}]`);
        const filename = fields[1] || fields[0] || 'source';
        return `<span style="display:inline;padding:1px 5px;margin:0 1px;font-size:11px;color:#78716c;background:#f5f3f1;border:1px solid #D6D3D1;border-radius:4px;white-space:nowrap">📄 ${filename}</span>`;
      } catch {
        const filenameMatch = inner.match(/,\s*"([^"]+\.pdf)"/i);
        const name = filenameMatch ? filenameMatch[1] : 'citation';
        return `<span style="display:inline;padding:1px 5px;margin:0 1px;font-size:11px;color:#78716c;background:#f5f3f1;border:1px solid #D6D3D1;border-radius:4px;white-space:nowrap">📄 ${name}</span>`;
      }
    },
  );

  // Strip ::button:id:: tokens
  result = result.replace(/::\w+:[^\s:]+::/g, '');

  return result;
}

export function MessageBubble({ message }: MessageBubbleProps) {
  const isUser = message.role === 'user';
  const isThinking = message.isThinking;
  const content = isUser
    ? message.content
    : transformInlineTokens(stripSubmitTokens(message.content));

  if (isThinking) {
    return (
      <div className="flex justify-start">
        <div className="text-[13px] leading-[20px] text-[#78716C] break-words">
          {content}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`text-[13px] leading-[20px] break-words overflow-hidden ${
          isUser
            ? 'max-w-[85%] bg-[#f5f3f1] text-[#1C1917] rounded-[8px] px-[12px] py-[12px]'
            : 'w-full text-[#1C1917]'
        }`}
      >
        {isUser ? (
          message.content
        ) : (
          <Markdown
            remarkPlugins={[remarkGfm, remarkBreaks]}
            rehypePlugins={[rehypeRaw]}
            components={{
              a: ({ href, children }) => (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#77010D] underline hover:text-[#5C0109]"
                >
                  {children}
                </a>
              ),
              h2: ({ children }) => (
                <h2 className="text-[14px] font-semibold text-[#1C1917] mt-[16px] mb-[4px]">{children}</h2>
              ),
              h3: ({ children }) => (
                <h3 className="text-[13px] font-semibold text-[#1C1917] mt-[12px] mb-[4px]">{children}</h3>
              ),
              p: ({ children }) => (
                <p className="my-[4px] leading-[20px]">{children}</p>
              ),
              ul: ({ children }) => (
                <ul className="list-disc pl-[20px] my-[4px] space-y-[2px]">{children}</ul>
              ),
              ol: ({ children }) => (
                <ol className="list-decimal pl-[20px] my-[4px] space-y-[2px]">{children}</ol>
              ),
              li: ({ children }) => (
                <li className="leading-[20px]">{children}</li>
              ),
              code: ({ children, className }) => {
                const isBlock = className?.includes('language-');
                return isBlock ? (
                  <pre className="bg-[#f5f3f1] rounded-[6px] px-[12px] py-[10px] my-[8px] overflow-x-auto text-[12px]">
                    <code>{children}</code>
                  </pre>
                ) : (
                  <code className="bg-[#f5f3f1] px-[4px] py-[2px] rounded-[3px] text-[12px]">{children}</code>
                );
              },
              strong: ({ children }) => (
                <strong className="font-semibold text-[#1C1917]">{children}</strong>
              ),
              table: ({ children }) => (
                <div className="overflow-x-auto my-[8px]">
                  <table className="text-[12px] border-collapse w-full">{children}</table>
                </div>
              ),
              th: ({ children }) => (
                <th className="text-left px-[8px] py-[4px] border-b border-[#D6D3D1] font-semibold text-[#78716c]">{children}</th>
              ),
              td: ({ children }) => (
                <td className="px-[8px] py-[4px] border-b border-[#f0eeec]">{children}</td>
              ),
            }}
          >
            {content}
          </Markdown>
        )}
      </div>
    </div>
  );
}
